// src/services/sseParametersManager.js
// *******************************************************************************
// SSE PARAMETERS MANAGER
// *******************************************************************************
// Manages Server-Sent Events connection for real-time application parameters
// Handles automatic reconnection, error recovery, and parameter updates

import { getStorageAuthItem } from 'stores/auth';
import { API } from 'constants/Api';
import { mergeUrlParamsQuery } from 'utils/urlUtils';
import consoleX from 'utils/consoleX';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

const RECONNECT_INTERVALS = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff
const MAX_RECONNECT_ATTEMPTS = 5;
const PING_TIMEOUT = 45000; // 45 seconds (backend sends ping every 30s)
const FILE_TITLE = 'SSE PARAMETERS';

// *******************************************************************************
// STATE
// *******************************************************************************
let eventSource = null;
let reconnectAttempts = 0;
let reconnectTimeoutId = null;
let pingTimeoutId = null;
let isManualClose = false;
let consecutiveErrors = 0; // Track consecutive connection errors
let lastErrorTime = 0; // Track when last error occurred
let initializationTime = 0; // Track when SSE was initialized
let currentCallbacks = {
  onUpdate: null,
  onConnect: null,
  onError: null,
};

// *******************************************************************************
// HELPER FUNCTIONS
// *******************************************************************************

/**
 * Get backend base URL from axios config
 */
const getBackendUrl = async () => {
  try {
    const { default: apiConfig } = await import('configs/fetchInterceptor');
    return apiConfig.defaults.baseURL || '';
  } catch (err) {
    consoleX({
      message: 'Failed to Get Backend URL',
      emoji: '🚫',
      flag: 'e',
      title: FILE_TITLE,
      info: {
        Error: err.message,
      },
    });
    return '';
  }
};

/**
 * Build SSE URL with authentication and filters
 *
 * SSE connections require accountId and optionally subscriptionId:
 * - accountId: Required - returns application scope parameters (theme, preferences)
 * - subscriptionId: Optional - when present, also returns module scope parameters
 */
const buildSSEUrl = async (filters = {}) => {
  const token = getStorageAuthItem('token');

  if (!token) {
    throw new Error('No authentication token found');
  }

  // Get backend URL from axios config
  const backendUrl = await getBackendUrl();

  // Build query parameters
  // accountId is required for application-level parameters (user preferences)
  // subscriptionId is required for module-level parameters (when user has subscription)
  const query = {
    token,
    ...(filters.accountId && { accountId: filters.accountId }),
    ...(filters.subscriptionId && { subscriptionId: filters.subscriptionId }),
    ...(filters.moduleId && { moduleId: filters.moduleId }),
  };

  // Use urlUtils to merge URL with query parameters
  const relativeUrl = mergeUrlParamsQuery({
    url: API.PARAMETERS.STREAM,
    query,
  });

  // Return absolute URL (backendUrl + relative path)
  const fullUrl = `${backendUrl}${relativeUrl}`;

  return fullUrl;
};

/**
 * Reset ping timeout
 */
const resetPingTimeout = () => {
  if (pingTimeoutId) {
    clearTimeout(pingTimeoutId);
  }

  pingTimeoutId = setTimeout(() => {
    const setAppStateStore = useZustand('app', 'setState', false);

    consoleX({
      message: 'Ping Timeout - Connection May Be Dead',
      emoji: '⏱️',
      flag: 'w',
      title: FILE_TITLE,
      info: {
        'Timeout Duration': `${PING_TIMEOUT}ms`,
        Action: 'Closing connection and scheduling reconnect',
      },
    });

    if (eventSource && !isManualClose) {
      setAppStateStore({ sseConnected: false });
      eventSource.close();
      scheduleReconnect();
    }
  }, PING_TIMEOUT);
};

/**
 * Schedule reconnection with exponential backoff
 */
const scheduleReconnect = () => {
  if (isManualClose) return;

  if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
    if (currentCallbacks.onError) {
      currentCallbacks.onError({
        type: 'max_reconnect_attempts',
        message: 'Failed to reconnect after maximum attempts',
      });
    }
    return;
  }

  const delay = RECONNECT_INTERVALS[Math.min(reconnectAttempts, RECONNECT_INTERVALS.length - 1)];

  consoleX({
    message: 'Scheduling Reconnection',
    emoji: '🔄',
    flag: 'i',
    title: FILE_TITLE,
    info: {
      'Delay (ms)': delay,
      Attempt: `${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS}`,
      'Backoff Strategy': 'Exponential',
    },
  });

  reconnectTimeoutId = setTimeout(() => {
    reconnectAttempts++;
    initSSE(currentCallbacks.onUpdate, currentCallbacks.onConnect, currentCallbacks.onError);
  }, delay);
};

/**
 * Clear all timeouts
 */
const clearTimeouts = () => {
  if (reconnectTimeoutId) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }

  if (pingTimeoutId) {
    clearTimeout(pingTimeoutId);
    pingTimeoutId = null;
  }
};

// *******************************************************************************
// EVENT HANDLERS
// *******************************************************************************

/**
 * Handle connection open
 */
const handleOpen = () => {
  // consoleX({
  //   message: 'Connection Opened Successfully',
  //   emoji: '🔗',
  //   flag: 's',
  //   title: FILE_TITLE,
  //   info: {
  //     ReadyState: eventSource?.readyState,
  //     URL: eventSource?.url?.replace(/token=([^&]+)/, 'token=***'),
  //     'Reconnect Attempts Reset': true,
  //     'Consecutive Errors Reset': true,
  //   },
  // });
  reconnectAttempts = 0;
  consecutiveErrors = 0; // Reset on successful connection
  resetPingTimeout();
};

/**
 * Handle 'connected' event
 */
const handleConnected = event => {
  try {
    const data = JSON.parse(event.data);

    if (currentCallbacks.onConnect) {
      currentCallbacks.onConnect(data);
    }

    resetPingTimeout();
  } catch (err) {
    consoleX({
      message: 'Error Parsing Connected Event',
      emoji: '❌',
      flag: 'e',
      title: FILE_TITLE,
      info: {
        Error: err.message,
        'Raw Data': event.data,
      },
    });
  }
};

/**
 * Handle 'initial' event (first parameter load)
 */
const handleInitial = event => {
  try {
    const data = JSON.parse(event.data);

    if (currentCallbacks.onUpdate) {
      currentCallbacks.onUpdate(data.parameters, 'initial');
    }

    resetPingTimeout();
  } catch (err) {
    consoleX({
      message: 'Error Parsing Initial Event',
      emoji: '❌',
      flag: 'e',
      title: FILE_TITLE,
      info: {
        Error: err.message,
        'Raw Data': event.data,
      },
    });
  }
};

/**
 * Handle the 'update' event (parameter change)
 */
const handleUpdate = event => {
  try {
    const data = JSON.parse(event.data);

    if (currentCallbacks.onUpdate) {
      currentCallbacks.onUpdate(data.parameters, 'update');
    }

    resetPingTimeout();
  } catch (err) {
    consoleX({
      message: 'Error Parsing Update Event',
      emoji: '❌',
      flag: 'e',
      title: FILE_TITLE,
      info: {
        Error: err.message,
        'Raw Data': event.data,
      },
    });
  }
};

/**
 * Handle 'ping' event (keep-alive)
 */
const handlePing = () => {
  // Silent ping - just reset timeout
  resetPingTimeout();
};

/**
 * Handle 'error' event from SSE
 */
const handleError = event => {
  try {
    const data = event.data ? JSON.parse(event.data) : null;

    if (currentCallbacks.onError) {
      currentCallbacks.onError({
        type: 'sse_error',
        message: data?.message || 'SSE error occurred',
        details: data,
      });
    }

    resetPingTimeout();
  } catch (err) {
    consoleX({
      message: 'Error Parsing SSE Error Event',
      emoji: '❌',
      flag: 'e',
      title: FILE_TITLE,
      info: {
        Error: err.message,
        'Raw Data': event.data,
      },
    });
  }
};

/**
 * Handle EventSource error (connection issues)
 */
const handleConnectionError = error => {
  clearTimeouts();

  if (!isManualClose) {
    const now = Date.now();
    const timeSinceInit = now - initializationTime;

    // Track consecutive errors (if errors happen within 5 seconds, count as consecutive)
    if (now - lastErrorTime < 5000) {
      consecutiveErrors++;
    } else {
      consecutiveErrors = 1;
    }
    lastErrorTime = now;

    // Check if EventSource is in CLOSED state (permanent error like 401, 403, 404)
    // EventSource.CLOSED = 2 means connection failed and won't retry automatically
    const isPermanentError = eventSource?.readyState === EventSource.CLOSED;

    // If error happens within 1 second of initialization, it's an immediate failure (server offline)
    const isImmediateFailure = timeSinceInit < 1000;

    // If we've reached max reconnect attempts, stop trying
    const hasReachedMaxAttempts = reconnectAttempts >= MAX_RECONNECT_ATTEMPTS;

    // Also treat as permanent if we've had 3+ consecutive fast errors
    const isFastFailureLoop = consecutiveErrors >= 3;

    const shouldStopRetrying =
      isPermanentError || isFastFailureLoop || isImmediateFailure || hasReachedMaxAttempts;

    if (currentCallbacks.onError) {
      currentCallbacks.onError({
        type: shouldStopRetrying ? 'permanent_error' : 'connection_error',
        message: shouldStopRetrying
          ? 'SSE connection failed (authentication, server offline, or configuration error)'
          : 'SSE connection error',
        details: error,
        consecutiveErrors,
        reconnectAttempts,
        timeSinceInit,
      });
    }

    // Don't retry on permanent errors (401, 403, 404, server offline, etc)
    if (shouldStopRetrying) {
      const setAppStateStore = useZustand('app', 'setState', false);

      let reason = '';
      if (isPermanentError) {
        reason = 'EventSource is in CLOSED state (likely 401 Unauthorized or 404 Not Found)';
      } else if (isImmediateFailure) {
        reason = `Connection failed immediately (${timeSinceInit}ms after init) - likely server offline or ERR_CONNECTION_REFUSED`;
      } else if (hasReachedMaxAttempts) {
        reason = `Maximum reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) reached`;
      } else {
        reason = `${consecutiveErrors} consecutive fast connection failures detected`;
      }

      consoleX({
        message: 'Permanent Connection Error - Will Not Retry',
        emoji: '⚠️',
        flag: 'w',
        title: FILE_TITLE,
        info: {
          Reason: reason,
          ReadyState: eventSource?.readyState,
          'Consecutive Errors': consecutiveErrors,
          'Reconnect Attempts': reconnectAttempts,
          'Time Since Init (ms)': timeSinceInit,
          'App Behavior': 'Will continue without real-time parameter updates',
          'Action Required': 'Check backend logs and ensure SSE endpoint is running and accessible',
        },
      });

      isManualClose = true; // Prevent further reconnection attempts
      consecutiveErrors = 0; // Reset for future attempts
      reconnectAttempts = 0; // Reset attempts
      if (eventSource) {
        setAppStateStore({ sseConnected: false });
        eventSource.close();
        eventSource = null;
      }
    } else {
      // Only retry on transient errors (network issues, etc)
      scheduleReconnect();
    }
  }
};

// *******************************************************************************
// PUBLIC API
// *******************************************************************************

/**
 * Initialize SSE connection for parameters
 *
 * @param {Function} onUpdate - Callback when parameters update (receives parameters, type)
 * @param {Function} onConnect - Callback when connection established (receives connection info)
 * @param {Function} onError - Callback when error occurs (receives error object)
 * @param {Object} filters - Optional filters (moduleId, subscriptionId)
 * @returns {Promise<void>}
 */
export const initSSE = async (onUpdate, onConnect, onError, filters = {}) => {
  const setAppStateStore = useZustand('app', 'setState', false);

  // Close existing connection
  if (eventSource) {
    closeSSE();
  }

  // Store callbacks
  currentCallbacks = {
    onUpdate: onUpdate || null,
    onConnect: onConnect || null,
    onError: onError || null,
  };

  // Reset state
  isManualClose = false;
  initializationTime = Date.now(); // Track when we started

  try {
    const url = await buildSSEUrl(filters);

    eventSource = new EventSource(url);

    // Connection events
    eventSource.onopen = handleOpen;
    eventSource.onerror = handleConnectionError;

    // Custom events
    eventSource.addEventListener('connected', handleConnected);
    eventSource.addEventListener('initial', handleInitial);
    eventSource.addEventListener('update', handleUpdate);
    eventSource.addEventListener('ping', handlePing);
    eventSource.addEventListener('error', handleError);

    // Handle generic SSE messages (backend doesn't set event type)
    eventSource.onmessage = event => {
      try {
        const data = JSON.parse(event.data);

        // Route message based on type field in data
        switch (data.type) {
          case 'connected':
            consoleX({
              message: 'Connection Established',
              emoji: '☑️',
              flag: 's',
              title: FILE_TITLE,
              info: {
                Event: 'connected',
                ReadyState: eventSource?.readyState,
                URL: eventSource?.url?.replace(/token=([^&]+)/, 'token=***'),
                'Reconnect Attempts Reset': true,
                'Consecutive Errors Reset': true,
                Data: data,
              },
            });

            if (currentCallbacks.onConnect) {
              currentCallbacks.onConnect(data);
              setAppStateStore({ sseConnected: true });
            }
            break;

          case 'initial':
            consoleX({
              message: 'Message Received',
              emoji: '📦',
              flag: 'progress',
              title: FILE_TITLE,
              info: {
                Event: 'initial',
                Timestamp: new Date().toISOString(),
                'Data Object': data.parameters,
                Data: JSON.stringify(data.parameters, null, 2),
              },
            });
            if (currentCallbacks.onUpdate && data.parameters) {
              currentCallbacks.onUpdate(data.parameters, 'initial');
            }
            break;

          case 'update':
            consoleX({
              message: 'Parameter Update Received',
              emoji: '🔄',
              flag: 'progress',
              title: FILE_TITLE,
              info: {
                Event: 'update',
                Scope: data.scope,
                'Module ID': data.moduleId || 'N/A',
                Category: data.category,
                Key: data.key,
                'New Value': data.value,
                Timestamp: data.timestamp,
              },
            });

            // Call onUpdate with the change details
            if (currentCallbacks.onUpdate) {
              currentCallbacks.onUpdate(data, 'update');
            }
            break;

          case 'ping':
            // Silent ping - don't log
            break;

          default:
            consoleX({
              message: 'Unknown SSE Message Type',
              emoji: '❓',
              flag: 'w',
              title: FILE_TITLE,
              info: {
                'Unknown Type': data.type,
                Data: data,
              },
            });
            break;
        }

        resetPingTimeout();
      } catch (err) {
        consoleX({
          message: 'Error Parsing SSE Message',
          emoji: '❌',
          flag: 'e',
          title: FILE_TITLE,
          info: {
            Error: err.message,
            'Raw Data': event.data,
          },
        });
      }
    };

    consoleX({
      message: 'Initializing Server-Sent Events Connection',
      emoji: '🚀',
      flag: 'i',
      title: FILE_TITLE,
      info: {
        Listeners: 'connected, initial, update, ping, error',
        'OnMessage Handler': 'Generic SSE message router',
        URL: eventSource?.url?.replace(/token=([^&]+)/, 'token=***'),
        'Reconnect Attempts Reset': true,
        'Consecutive Errors Reset': true,
      },
    });
  } catch (err) {
    setAppStateStore({ sseConnected: false });

    consoleX({
      message: 'Failed to Initialize SSE',
      emoji: '🚫',
      flag: 'e',
      title: FILE_TITLE,
      info: {
        Error: err.message,
      },
    });

    if (onError) {
      onError({
        type: 'initialization_error',
        message: 'Failed to initialize SSE connection',
        details: err,
      });
    }
  }
};

/**
 * Close SSE connection and cleanup
 *
 * @returns {void}
 */
export const closeSSE = () => {
  consoleX({
    message: 'SSE Connection Closed',
    emoji: '🚫',
    flag: 'i',
    title: FILE_TITLE,
    info: {
      'Manual Close': true,
      'Timeouts Cleared': true,
      'State Reset': 'All counters and callbacks cleared',
    },
  });

  isManualClose = true;
  clearTimeouts();

  if (eventSource) {
    const setAppStateStore = useZustand('app', 'setState', false);
    setAppStateStore({ sseConnected: false });
    eventSource.close();
    eventSource = null;
  }

  reconnectAttempts = 0;
  consecutiveErrors = 0; // Reset error tracking
  lastErrorTime = 0;
  initializationTime = 0;
  currentCallbacks = {
    onUpdate: null,
    onConnect: null,
    onError: null,
  };
};

/**
 * Get current connection state
 *
 * @returns {Object} Connection state
 */
export const getConnectionState = () => {
  return {
    connected: eventSource?.readyState === EventSource.OPEN,
    readyState: eventSource?.readyState,
    reconnectAttempts,
    isManualClose,
  };
};

/**
 * Force reconnection (useful for token refresh)
 *
 * @param {Object} filters - Optional filters (moduleId, subscriptionId)
 * @returns {void}
 */
export const reconnect = (filters = {}) => {
  consoleX({
    message: 'Manual Reconnection Requested',
    emoji: '🔄',
    flag: 'i',
    title: FILE_TITLE,
    info: {
      Filters: filters,
      'Previous Reconnect Attempts': reconnectAttempts,
    },
  });

  // Save callbacks BEFORE closeSSE resets them to null
  const savedCallbacks = { ...currentCallbacks };

  reconnectAttempts = 0;
  closeSSE();
  initSSE(
    savedCallbacks.onUpdate,
    savedCallbacks.onConnect,
    savedCallbacks.onError,
    filters
  ).then();
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default {
  initSSE,
  closeSSE,
  getConnectionState,
  reconnect,
};
